/**
 * A KPI card that is also the filter for the set it counts.
 *
 * The point is that the number and the list it opens are the SAME query: a card
 * saying "Zu prüfen 11" is only useful if clicking it shows those eleven, and it
 * is actively misleading if it shows something else. So a card carries the filter
 * value it stands for, and the page hands that value straight to the endpoint.
 *
 * A real `<button>` rather than a clickable div: it changes what the table shows,
 * so it has to be reachable and operable from the keyboard and carry an accessible
 * name that says what it does — "Zu prüfen: 11, filtern" rather than a bare number.
 *
 * Non-interactive by design when `onSelect` is absent, so the same component can
 * render a figure that has no honest filter behind it.
 */
import React from "react";

const TONE = {
  neutral: "bg-muted text-muted-foreground",
  primary: "bg-primary/10 text-primary",
  good: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400",
  warn: "bg-amber-50 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300",
  bad: "bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300",
};

export default function BexioKpiFilterCard({
  icon: Icon,
  tone = "primary",
  label,
  value,
  hint,
  active = false,
  onSelect,
  selectHint = "filtern",
}) {
  const body = (
    <>
      <span
        aria-hidden="true"
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
          TONE[tone] || TONE.primary
        }`}
      >
        {Icon ? <Icon className="h-5 w-5" /> : null}
      </span>
      <div className="min-w-0">
        <div className="text-xs font-medium text-muted-foreground">{label}</div>
        <div className="mt-1 text-xl font-semibold leading-tight text-foreground">
          {value}
        </div>
        {hint ? (
          <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>
        ) : null}
      </div>
    </>
  );

  if (!onSelect) {
    return (
      <div className="flex items-start gap-3 rounded-xl border border-border bg-card p-4">
        {body}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      aria-label={`${label}: ${value}, ${selectHint}`}
      className={`flex items-start gap-3 rounded-xl border bg-card p-4 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:bg-muted ${
        active ? "border-primary ring-1 ring-primary" : "border-border"
      }`}
    >
      {body}
    </button>
  );
}
